import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuill } from 'react-quilljs'
import { useParams } from 'react-router-dom'
import { Form, notification } from 'antd'
import axios from 'axios'
import Const from 'constants/constant'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import useGetAnnotationColor from 'hooks/useGetAnnotationColor'
import useMutation from 'hooks/useMutation'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  hasLinkErrorState,
  listPointState,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import { currentAnnotation } from 'store/krpanoStore'
import useSWR from 'swr'
import { AlertPayload } from 'types/alert'
import {
  Annotation,
  AnnotationType,
  BuildingFile,
  LinkAnnotation
} from 'types/building'
import { KrpanoInterface } from 'types/krpano'
import Utils from 'utils'
import Resizer from 'utils/imageResizer'
import Krpano from 'utils/krpano'
import {
  getPointVersion,
  handlePointVersionError,
  syncPointVersion
} from 'utils/pointVersion'

import { HandoverName } from '../types'

export default function useBcpAnnotation() {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const { navigate } = useDirectory()
  const { getAnnotationBgColor } = useGetAnnotationColor()
  const [memoContent, setMemoContent] = useState('')

  // quill for 申し送り
  const { quill: quillHandover, quillRef: quillRefHandover } = useQuill({
    theme: 'snow',
    modules: Const.QUILL_MODULES,
    formats: Const.QUILL_FORMATS,
    placeholder: '入力してください'
  })

  const [selectedType, setSelectedType] = useState<AnnotationType>(
    AnnotationType.FIRE
  )
  const [disableWhenMemoNotCompleted, setDisableWhenMemoNotCompleted] =
    useState(true)
  const [selectedTabs, setSelectedTabs] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [isChangeType, setIsChangeType] = useState(false)
  const [nextType, setNextType] = useState<AnnotationType | undefined>(
    undefined
  )
  const [isDeleting, setIsDeleting] = useState(false)
  const [nameParse, setNameParse] = useState<string[]>(['', '', ''])
  const [linkParse, setLinkParse] = useState<string[]>(['', '', ''])
  const [memoParse, setMemoParse] = useState<string[]>(['', '', ''])
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingContent, setIsFetchingContent] = useState(false)
  const [previewFileIndex, setPreviewFileIndex] = useState<number | undefined>(
    undefined
  )
  const [tempAlertData, setTempAlertData] = useState<AlertPayload>()
  // state for name of HandoverName memo
  const [handoverName, setHandoverName] = useState<HandoverName>({
    handoverFileName: '',
    handoverOccurenceFileName: '',
    handoverRecoveryFileName: ''
  })

  const [hasHandoverError, setHasHandoverError] = useState(false)
  const hasLinkError = useRecoilValue(hasLinkErrorState)
  const setHasLinkError = useSetRecoilState(hasLinkErrorState)
  const [form] = Form.useForm()
  const [formHandover] = Form.useForm()

  const [memoName, setMemoName] = useState('')

  const selectedSubImage = useRecoilValue(selectedSubImageState)
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const [currentSelected, setCurrentSelected] =
    useRecoilState(currentAnnotation)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const setPoints = useSetRecoilState(listPointState)

  const annotationBgColor = useMemo(
    () => getAnnotationBgColor(selectedType),
    [getAnnotationBgColor, selectedType]
  )

  const listMemos = useMemo(
    () => [
      currentSelected?.memo,
      currentSelected?.memo_occurence,
      currentSelected?.memo_recovery
    ],
    [
      currentSelected?.memo,
      currentSelected?.memo_occurence,
      currentSelected?.memo_recovery
    ]
  )

  const isDataExistsAnnotation = Krpano.IsAnnotationDataEmpty(currentSelected)

  const { trigger, isMutating } = useMutation(`v1/annotations`)
  const { trigger: getPresignedUrl, isMutating: isGettingPresignedUrl } =
    useMutation('v1/annotations/presigned-urls')
  const { trigger: deleteOldContent, isMutating: isDeletingOldContent } =
    useMutation('v1/delete-file')
  const { trigger: createAlert } = useMutation('v1/alerts')

  const { trigger: getBuildingPresignedUrl } = useMutation(
    'v1/buildings/presigned-urls'
  )
  const { trigger: saveFileMetadata } = useMutation('v1/buildings/files')

  // const linksStageValues = Form.useWatch('linksStage', form)

  // We now track link errors in BCPAnnotationCategoryDetail.tsx via onFieldsChange
  // so we can remove this useEffect which was acting early.
  //
  // useEffect(() => {
  //   if (selectedCategory === 'link') { ... }
  // }, ...)

  const { data: bcpCustomFolders } = useSWR({
    url: `v1/bcp/buildings/${id}/folders`
  })

  const annotationList = useMemo(
    () => [
      ...Const.BCP_ANNOTATION_LIST,
      ...(bcpCustomFolders || []).map(
        (item: { categoryId: number; name: string }) => ({
          value: item.categoryId,
          label: item.name
        })
      )
    ],
    [bcpCustomFolders]
  )

  const listTabs = useMemo((): { value: number; label: string }[] => {
    if (Object.values(AnnotationType).includes(selectedType || '')) {
      return Const.BCP_ANNOTATION_TABS.map((item) => ({
        value: selectedType + item.value,
        label: item.label
      }))
    }
    return (
      bcpCustomFolders
        ?.find(
          (item: { categoryId: number }) => item.categoryId === selectedType
        )
        ?.childFolders?.map((item: { categoryId: number; name: string }) => ({
          value: item.categoryId,
          label: item.name
        })) || []
    )
  }, [bcpCustomFolders, selectedType])

  const selectedTabIndex = useMemo(
    () =>
      listTabs.findIndex(
        (item: { value: number; label: string }) => item.value === selectedTabs
      ),
    [listTabs, selectedTabs]
  )

  const prevHandoverTabRef = useRef<number | null>(null)

  const changeSelectedType = useCallback(
    (value: AnnotationType) => {
      if (value !== selectedType) {
        if (!isDataExistsAnnotation) {
          setNextType(value)
          setIsChangeType(true)
        } else {
          setCurrentSelected((prev) => ({
            ...prev,
            type: value
          }))
          setSelectedType(value)
          setSelectedTabs(listTabs[0].value)
          // Reset memoName if not MEMO type
          if (value !== AnnotationType.MEMO) {
            setMemoName('')
          }
        }
      }
    },
    [isDataExistsAnnotation, listTabs, selectedType, setCurrentSelected]
  )

  const handleCloseModal = () => {
    setCurrentSelected(undefined)
    Krpano.RemoveTempAnnotation()
    setTempAlertData(undefined)
    notification.destroy('placingAnnotation')
  }

  const hotspotLoadStyle = (krpano: KrpanoInterface, data: Annotation) => {
    krpano.call(`remove_annotation(${data.id})`)
    Krpano.AddAnnotation(krpano, [data])
  }

  const createNewAnnotation = (krpano: KrpanoInterface, data: Annotation) => {
    krpano.call('hide_temp_hotspot()')
    Krpano.AddAnnotation(krpano, [data])
  }

  const getLinkContent = () => {
    if (selectedCategory === 'link') {
      const links = form.getFieldValue(['linksStage', selectedTabs])
      const firstLink =
        Array.isArray(links) && links.length > 0 ? links[0]?.link || '' : ''
      return linkParse.map((item: string, index: number) =>
        index === selectedTabIndex ? firstLink : item
      )
    }
    return linkParse
  }

  const getNameContent = () => {
    if (selectedCategory === 'link') {
      const links = form.getFieldValue(['linksStage', selectedTabs])
      const firstName =
        Array.isArray(links) && links.length > 0 ? links[0]?.name || '' : ''
      return nameParse.map((item: string, index: number) =>
        index === selectedTabIndex ? firstName : item
      )
    }
    return nameParse
  }

  const getMemoContent = (): string[] => {
    if (selectedCategory === 'handover') {
      return memoParse.map((item: string, index: number) =>
        index === selectedTabIndex ? quillHandover?.root.innerHTML || '' : item
      )
    }
    return memoParse
  }

  const handleFillParseValue = () => {
    setNameParse(getNameContent())
    setLinkParse(getLinkContent())
    setMemoParse(getMemoContent())
  }

  // Update form values when selectedCategory or selectedTabs changes
  useEffect(() => {
    if (selectedCategory === 'link' && selectedTabs) {
      const link = linkParse[selectedTabIndex] || ''
      const name = nameParse[selectedTabIndex] || ''
      form.setFieldsValue({
        name,
        link
      })
      form.validateFields(['link', 'name'])
    }
  }, [
    form,
    linkParse,
    nameParse,
    selectedCategory,
    selectedTabIndex,
    selectedTabs
  ])

  const getHandoverNameField = useCallback((index: number): string => {
    switch (index) {
      case 0:
        return 'handoverFileName'
      case 1:
        return 'handoverOccurenceFileName'
      case 2:
        return 'handoverRecoveryFileName'
      default:
        return ''
    }
  }, [])

  const validateForm = useCallback(
    (
      quillContent?: string,
      isHandoverTab: boolean = false,
      overrideName?: string
    ): boolean => {
      const field = getHandoverNameField(selectedTabIndex)
      if (isHandoverTab) {
        const nameContent = overrideName ?? formHandover.getFieldValue(field)
        const isNameEmpty = !nameContent?.trim()
        const isNameValid =
          nameContent?.trim()?.length >= 1 && nameContent?.trim()?.length <= 100
        const html = quillContent ?? quillHandover?.root?.innerHTML ?? ''
        const htmlEmpty = Krpano.IsMemoEmpty(html)
        const plainText = quillHandover?.getText?.() || ''
        const textEmpty = plainText.replace(/\s|\n/g, '').length === 0
        const isContentEmpty = htmlEmpty && textEmpty
        const hasError = isNameEmpty || !isNameValid || isContentEmpty
        setHasHandoverError(hasError)
        if (isContentEmpty && !isNameValid) {
          formHandover.resetFields()
          return false
        }

        return !hasError
      }
      const nameContent = formHandover.getFieldValue(field)
      const html = quillContent ?? quillHandover?.root?.innerHTML ?? ''
      const htmlEmpty = Krpano.IsMemoEmpty(html)
      const plainText = quillHandover?.getText?.() || ''
      const textEmpty = plainText.replace(/\s|\n/g, '').length === 0
      const isMemoEmpty = htmlEmpty && textEmpty
      const nameValid =
        nameContent?.trim()?.length >= 1 && nameContent?.trim()?.length <= 100
      const isFormValid = Boolean(memoName) && nameValid && !isMemoEmpty

      if (setDisableWhenMemoNotCompleted) {
        setDisableWhenMemoNotCompleted(!isFormValid)
      }

      return isFormValid
    },
    [
      formHandover,
      memoName,
      selectedTabIndex,
      setDisableWhenMemoNotCompleted,
      getHandoverNameField,
      quillHandover
    ]
  )

  const handleCategoryBack = () => {
    handleFillParseValue()
    setSelectedCategory('')
    formHandover.resetFields()
  }

  const handleChangeTab = (tab: number) => {
    handleFillParseValue()
    setSelectedTabs(tab)
  }

  const hanldeUploadMemoOrHandover = async () => {
    // memo anotation only
    if (selectedType === AnnotationType.MEMO) {
      if (Krpano.IsMemoEmpty(memoContent)) return [null, null, null]
      const newQuillInnerHtml = await Resizer.ResizedQuillImages(memoContent)
      const file = new Blob([newQuillInnerHtml], { type: 'text/plain' })
      if (file.size / (1024 * 1024) > 50) {
        notification.error({
          key: 'memoTooLarge',
          message: t('common.validationError.fileTooLarge')
        })
        return [null, null, null]
      }
      const data = await getPresignedUrl({
        data: { extension: 'txt', fileName: 'annotation-content' }
      })
      const { key = undefined, presignedUrl = undefined } = {
        ...data?.data?.s3PreSignedLinks[0]
      }
      await axios.put(presignedUrl, file)
      return [key, null, null]
    }
    // upload Handover(memo) for other anotation
    const promises = getMemoContent().map(async (item) => {
      if (item) {
        // On edit, quillHandover may be null. Use the HTML item directly for emptiness check.
        const isMemoEmpty = Krpano.IsMemoEmpty(item)
        if (isMemoEmpty) return null
        const newQuillInnerHtml = await Resizer.ResizedQuillImages(item)
        const file = new Blob([newQuillInnerHtml], {
          type: 'text/plain'
        })
        if (file.size / (1024 * 1024) > 50) {
          notification.error({
            key: 'memoTooLarge',
            message: t('common.validationError.fileTooLarge')
          })
          return null
        }
        const data = await getPresignedUrl({
          data: {
            extension: 'txt',
            fileName: 'annotation-content'
          }
        })
        const { key = undefined, presignedUrl = undefined } = {
          ...data?.data?.s3PreSignedLinks[0]
        }
        await axios.put(presignedUrl, file)
        return new Promise((resolve) => {
          resolve(key)
        })
      }
      return null
    })
    const results: (string | null | unknown)[] = await Promise.all(promises)
    return results as (string | null)[]
  }

  const handleDeleteOldContent = useCallback(async () => {
    if (currentSelected?.id) {
      await Promise.all(
        listMemos
          .filter((item): item is string => Boolean(item))
          .map(async (item) => {
            await deleteOldContent({
              data: { key: Krpano.GetMemoKeyFromPresignedUrl(item) }
            })
          })
      )
    }
  }, [currentSelected?.id, listMemos, deleteOldContent])

  const getImageID = useCallback((): string | number | undefined => {
    if (!pointDetail) return undefined

    const isViewingSubImage = pointDetail.subImages?.some(
      (item) => item.id.toString() === selectedSubImage?.toString()
    )

    return isViewingSubImage ? selectedSubImage : pointDetail.image360?.id
  }, [pointDetail, selectedSubImage])

  const getHandoverNameValue = useCallback(
    (index: number): string => {
      switch (index) {
        case 0:
          return handoverName.handoverFileName || ''
        case 1:
          return handoverName.handoverOccurenceFileName || ''
        case 2:
          return handoverName.handoverRecoveryFileName || ''
        default:
          return ''
      }
    },
    [handoverName]
  )

  const handleHandoverNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      const field = getHandoverNameField(selectedTabIndex)
      setHandoverName((prev) => ({
        ...prev,
        [field]: value
      }))
      formHandover.setFieldsValue({ [field]: value })
      // Trigger validation when handover name changes
      validateForm(quillHandover?.root?.innerHTML, true, value)
    },
    [
      selectedTabIndex,
      validateForm,
      quillHandover,
      getHandoverNameField,
      formHandover
    ]
  )

  const handleAddOrEditAnnotation = async () => {
    if (
      isMutating ||
      isGettingPresignedUrl ||
      isDeletingOldContent ||
      isFetchingContent
    )
      return

    const krpano = document.getElementById(
      'embedpano-full'
    ) as unknown as KrpanoInterface
    try {
      if (selectedType === AnnotationType.MEMO) {
        const memoNameTrimmed = memoName?.trim()
        if (!memoNameTrimmed) {
          notification.error({
            key: 'memoNameRequired',
            message: 'メモ名を入力してください'
          })
          return
        }
        const isMemoEmpty = Krpano.IsMemoEmpty(memoContent)
        if (isMemoEmpty) {
          notification.error({
            message: t('building.validationError.memoContentRequired')
          })
          return
        }
      }

      if (selectedCategory === 'link') {
        await form.validateFields()
      }
      setIsLoading(true)
      const isHandoverType = (type: number | string | undefined) => {
        return [
          AnnotationType.FIRE,
          AnnotationType.FLOOD,
          AnnotationType.EARTHQUAKE,
          AnnotationType.VOLCANO,
          AnnotationType.LIFE_LINE
        ].includes(Number(type))
      }

      if (isHandoverType(selectedType)) {
        const memoContents = getMemoContent()
        const pairs: [string, string][] = [
          [memoContents[0], handoverName.handoverFileName || ''],
          [memoContents[1], handoverName.handoverOccurenceFileName || ''],
          [memoContents[2], handoverName.handoverRecoveryFileName || '']
        ]

        const isEmptyHtml = (html: string) => {
          return Krpano.IsMemoEmpty(html)
        }

        const errorPairs: number[] = []

        pairs.forEach(([html, text], index) => {
          const htmlEmpty = isEmptyHtml(html)
          const textEmpty = !text || text.trim() === ''

          if (!htmlEmpty && textEmpty) {
            errorPairs.push(index)
          }

          if (htmlEmpty && !textEmpty) {
            errorPairs.push(index)
          }
        })

        const isValid = errorPairs.length === 0

        if (!isValid) {
          const errorTabs = errorPairs.map((i) => t(listTabs[i].label))

          const errorMessage = errorTabs.join(', ')

          notification.error({
            message: t('common.validationError.memoContentRequired', {
              tab: errorMessage
            })
          })

          setIsLoading(false)
          return
        }
      }

      handleDeleteOldContent()

      const uploadedKeys = await hanldeUploadMemoOrHandover()

      let finalFiles: BuildingFile[] | undefined = currentSelected?.files

      if (currentSelected?.files) {
        const pendingFiles = currentSelected.files.filter((f) => f.rawFile)
        if (pendingFiles.length > 0) {
          finalFiles = await Promise.all(
            currentSelected.files.map(async (fileItem) => {
              if (!fileItem.rawFile) return fileItem

              const file = fileItem.rawFile
              const category = fileItem.category || 0

              // 0. Extract width/height for images natively before upload
              let width: number | undefined
              let height: number | undefined

              if (file.type.startsWith('image/')) {
                const img = new Image()
                img.src = URL.createObjectURL(file)
                await new Promise((resolve) => {
                  img.onload = () => {
                    width = img.width
                    height = img.height
                    URL.revokeObjectURL(img.src)
                    resolve(true)
                  }
                  img.onerror = () => {
                    URL.revokeObjectURL(img.src)
                    resolve(true)
                  }
                })
              }

              // 1. Get Presigned URL
              const presignedData = await getBuildingPresignedUrl({
                data: {
                  buildingId: Number(id),
                  count: 1,
                  category
                }
              })

              const { presignedUrl, key: s3Key } =
                presignedData.data.s3PreSignLinks[0]

              // 2. Upload to S3
              await axios.put(presignedUrl, file)

              // 3. Save to Metadata API
              const savedFileData = await saveFileMetadata({
                data: {
                  buildingId: Number(id),
                  category,
                  extension: fileItem.name.split('.').pop(),
                  name: fileItem.name,
                  mimeType: file.type,
                  size: file.size,
                  s3Path: s3Key,
                  extraInfo:
                    width && height ? JSON.stringify({ width, height }) : null,
                  takenAt: dayjs(file.lastModified).format(
                    'YYYY-MM-DDTHH:mm:ssZ'
                  ),
                  overwriteID: fileItem.overwriteID,
                  mode: fileItem.mode
                }
              })

              const resData = savedFileData.data
              return {
                id: resData.id,
                name: resData.name || fileItem.name,
                s3Path: resData.s3_path || resData.s3Path || s3Key,
                mimeType: resData.mime_type || resData.mimeType || file.type,
                extension:
                  resData.extension ||
                  file.name.split('.').pop()?.toLowerCase() ||
                  '',
                createdAt:
                  resData.created_at ||
                  resData.createdAt ||
                  new Date().toISOString(),
                size: resData.size,
                thumbnail:
                  resData.thumbnail ||
                  resData.s3_path ||
                  resData.s3Path ||
                  s3Key,
                category,
                pointId: resData.point_id || resData.pointId || null,
                takenAt: resData.taken_at || resData.takenAt || null
              }
            })
          )
        }
      }

      if (currentSelected?.files) {
        const renamedFiles = currentSelected.files.filter(
          (f) => f.id > 0 && f.originalName && f.originalName !== f.name
        )
        if (renamedFiles.length > 0) {
          await Promise.all(
            renamedFiles.map((fileItem) =>
              saveFileMetadata({
                method: 'patch',
                query: [fileItem.id.toString()],
                data: { name: fileItem.name }
              })
            )
          )
        }
      }

      // Build per-tab link arrays from the flat internal links list
      const getTabLinks = (tabIdx: number) => {
        const tab = listTabs[tabIdx]
        if (!tab) return []

        let tabLinks: (LinkAnnotation & { category?: number | string })[] = []
        if (currentSelected && Array.isArray(currentSelected.links)) {
          tabLinks = currentSelected.links.filter(
            (l: (LinkAnnotation & { category?: number | string }) | null) =>
              l != null && l.category === tab.value
          )
        }

        if (tabIdx === 0) {
          return tabLinks.map((item: LinkAnnotation) => ({
            link: item?.link || '',
            name: item?.name || ''
          }))
        }
        if (tabIdx === 1) {
          return tabLinks.map((item: LinkAnnotation) => ({
            link_occurrence: item?.link || '',
            name_occurrence: item?.name || ''
          }))
        }
        return tabLinks.map((item: LinkAnnotation) => ({
          link_recovery: item?.link || '',
          name_recovery: item?.name || ''
        }))
      }

      const convertedValue = {
        ...(currentSelected || {}),
        memo_file_name: handoverName.handoverFileName,
        links: getTabLinks(0),
        link_occurrences: getTabLinks(1),
        link_recoveries: getTabLinks(2),
        // Remove individual properties as they're now arrays
        link: undefined,
        name: undefined,
        link_occurrence: undefined,
        name_occurrence: undefined,
        link_recovery: undefined,
        name_recovery: undefined,
        memo: uploadedKeys[0] === null ? '' : uploadedKeys[0],
        memo_occurence_file_name: handoverName.handoverOccurenceFileName,
        memo_occurence: uploadedKeys[1] === null ? '' : uploadedKeys[1],
        memo_recovery_file_name: handoverName.handoverRecoveryFileName,
        memo_recovery: uploadedKeys[2] === null ? '' : uploadedKeys[2],
        type: selectedType,
        files: finalFiles && finalFiles.map((item) => item.id),
        imageID: getImageID()
      }

      if (selectedType === AnnotationType.MEMO) {
        convertedValue.memo_name = memoName
      }

      const versionIdToSend = getPointVersion(currentSelectedPoint, pointDetail)
      const { data } = await trigger({
        method: 'put',
        headers: { 'X-Point-Version-ID': String(versionIdToSend) },
        data: {
          building_id: Number(id),
          floor_id: Number(floorId),
          point_id: currentSelectedPoint,
          annotation: convertedValue
        },
        onError: (error) => handlePointVersionError(error, t)
      })
      syncPointVersion(
        data.pointVersionId,
        currentSelectedPoint,
        setPointDetail,
        setPoints
      )
      setIsLoading(false)
      notification.success({
        message: t('building.saved')
      })
      if (!convertedValue.id) {
        setPointDetail(
          (prev) =>
            prev && {
              ...prev,
              pointVersionId: data.pointVersionId ?? prev.pointVersionId,
              annotations: [...(prev?.annotations || []), data]
            }
        )
        createNewAnnotation(krpano, data)
      } else {
        setPointDetail(
          (prev) =>
            prev && {
              ...prev,
              pointVersionId: data.pointVersionId ?? prev.pointVersionId,
              annotations: prev.annotations.map((item) =>
                item.id === data.id ? data : item
              )
            }
        )
        hotspotLoadStyle(krpano, data)
      }
      handleCloseModal()

      // If there is temporary alert data, save it now that we have an annotation ID
      if (tempAlertData) {
        try {
          // Exclude the draft-only extra_users_data before sending to the API
          const { extra_users_data: unused, ...apiPayload } = tempAlertData
          await createAlert({
            method: 'post',
            data: {
              ...apiPayload,
              annotation_id: data.id
            }
          })
          setTempAlertData(undefined)
        } catch (alertError) {
          notification.error({
            message: 'アラートの保存に失敗しました。'
          })
        }
      }
    } catch (error: unknown) {
      setIsLoading(false)
      if (krpano) {
        krpano.call('hide_temp_hotspot()')
      }
      notification.destroy('placingAnnotation')
      const err = error as { errorFields?: unknown[] }
      if (err?.errorFields) {
        console.error(error)
      } else {
        Utils.handleErrorNavigate(error, navigate)
      }
    }
  }

  const handleFetchContent = useCallback(async (): Promise<void> => {
    setIsFetchingContent(true)
    const results = await Promise.all(
      listMemos.map(async (item) => {
        if (!item) return ''
        try {
          return await Krpano.FetchMemoContent(item)
        } catch (error) {
          return ''
        }
      })
    )
    setMemoParse(results)
    setIsFetchingContent(false)
  }, [listMemos, setMemoParse])

  const handleMemoNameChange = (name: string) => {
    setMemoName(name)
    setCurrentSelected((prev) => ({
      ...prev,
      memo_name: name
    }))
  }

  useEffect(() => {
    handleFetchContent()
  }, [handleFetchContent])

  useEffect(() => {
    if (currentSelected?.type) {
      setSelectedType(currentSelected.type)
    }
  }, [currentSelected?.type])

  useEffect(() => {
    if (
      currentSelected?.name ||
      currentSelected?.name_occurrence ||
      currentSelected?.name_recovery
    ) {
      setNameParse([
        currentSelected?.name || '',
        currentSelected?.name_occurrence || '',
        currentSelected?.name_recovery || ''
      ])
    }
  }, [
    currentSelected?.name,
    currentSelected?.name_occurrence,
    currentSelected?.name_recovery
  ])

  useEffect(() => {
    if (
      currentSelected?.link ||
      currentSelected?.link_occurrence ||
      currentSelected?.link_recovery
    ) {
      setLinkParse([
        currentSelected?.link || '',
        currentSelected?.link_occurrence || '',
        currentSelected?.link_recovery || ''
      ])
    }
  }, [
    currentSelected?.link,
    currentSelected?.link_occurrence,
    currentSelected?.link_recovery
  ])

  useEffect(() => {
    if (listTabs.length) {
      setSelectedTabs(listTabs[0].value)
    }
  }, [listTabs])

  useEffect(() => {
    if (
      currentSelected?.memo_name &&
      currentSelected?.type === AnnotationType.MEMO
    ) {
      setMemoName(currentSelected.memo_name)
    } else {
      setMemoName('')
    }
  }, [currentSelected?.memo_name, currentSelected?.type])

  useEffect(() => {
    setHandoverName({
      handoverFileName: currentSelected?.memo_file_name || '',
      handoverOccurenceFileName:
        currentSelected?.memo_occurence_file_name || '',
      handoverRecoveryFileName: currentSelected?.memo_recovery_file_name || ''
    })
  }, [
    currentSelected?.memo_file_name,
    currentSelected?.memo_occurence_file_name,
    currentSelected?.memo_recovery_file_name
  ])

  // validate handover per active tab on quill content change
  useEffect(() => {
    if (!quillHandover || selectedCategory !== 'handover') return
    const handler = () => {
      // ensure Quill DOM updated before we read innerHTML
      requestAnimationFrame(() => {
        validateForm(quillHandover?.root?.innerHTML, true)
      })
    }
    quillHandover.on('text-change', handler)
    quillHandover.on('selection-change', handler)
    return () => {
      quillHandover.off('text-change', handler)
      quillHandover.off('selection-change', handler)
    }
  }, [quillHandover, selectedCategory, selectedTabIndex, validateForm])

  // effect fetch content for handover memo and overwrite placeholder
  useEffect(() => {
    if (selectedCategory === 'handover' && quillHandover) {
      const currentContent = quillHandover.root.innerHTML || ''
      const desiredContent = memoParse[selectedTabIndex] || ''
      const tabChanged = prevHandoverTabRef.current !== selectedTabIndex
      if (
        tabChanged ||
        (Krpano.IsMemoEmpty(currentContent) &&
          currentContent !== desiredContent)
      ) {
        quillHandover.root.innerHTML = tabChanged
          ? desiredContent
          : currentContent
      }
      prevHandoverTabRef.current = selectedTabIndex
      const field = getHandoverNameField(selectedTabIndex)
      const nameVal = getHandoverNameValue(selectedTabIndex)
      formHandover.setFieldsValue({
        [field]: nameVal || ''
      })
      validateForm(quillHandover?.root?.innerHTML, true)
      setTimeout(() => {
        let tooltipInput = null
        if (quillHandover && quillHandover.container) {
          if (quillHandover.container.parentNode) {
            tooltipInput = quillHandover.container.parentNode.querySelector(
              '.ql-tooltip input[data-link]'
            )
            if (!tooltipInput) {
              tooltipInput = quillHandover.container.querySelector(
                '.ql-tooltip input[data-link]'
              )
            }
          }
          if (tooltipInput) {
            tooltipInput.setAttribute('data-link', 'https://')
            tooltipInput.setAttribute('placeholder', 'https://')
          }
        }
      }, 100)
    }
  }, [
    memoParse,
    quillHandover,
    selectedCategory,
    selectedTabIndex,
    formHandover,
    getHandoverNameField,
    getHandoverNameValue,
    validateForm
  ])

  return {
    t,
    selectedType,
    changeSelectedType,
    annotationList,
    currentSelected,
    memoContent,
    setMemoContent,
    quillHandover,
    memoName,
    handleMemoNameChange,
    setDisableWhenMemoNotCompleted,
    annotationBgColor,
    selectedCategory,
    handleCategoryBack,
    listTabs,
    handleChangeTab,
    selectedTabIndex,
    setSelectedCategory,
    form,
    selectedTabs,
    linkParse,
    nameParse,
    formHandover,
    getHandoverNameField,
    getHandoverNameValue,
    handleHandoverNameChange,
    quillRefHandover,
    setIsDeleting,
    handleCloseModal,
    isLoading,
    handleAddOrEditAnnotation,
    disableWhenMemoNotCompleted,
    hasHandoverError,
    hasLinkError,
    setHasLinkError,
    isDeleting,
    isChangeType,
    nextType,
    setSelectedType,
    setIsChangeType,
    previewFileIndex,
    setPreviewFileIndex,
    setCurrentSelected,
    tempAlertData,
    setTempAlertData,
    isFetchingContent
  }
}
